import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

import { quoteFilesApi, type QuoteFile } from "@/lib/quote-files";
import { Button } from "@/components/ui/button";
import { FileDropzone } from "@/components/file-dropzone";
import { FileThumbnail } from "@/components/files/FileThumbnail";

export type QuoteFilesTabProps = {
  quoteId: number;
  canUpload: boolean;
  canDelete: boolean;
};

export function QuoteFilesTab({ quoteId, canUpload, canDelete }: QuoteFilesTabProps) {
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<QuoteFile | null>(null);

  const list = useQuery({
    queryKey: ["quote", quoteId, "files"],
    queryFn: () => quoteFilesApi.list(quoteId),
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) {
        await quoteFilesApi.upload(quoteId, f);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", quoteId, "files"] });
      toast.success("File(s) uploaded");
    },
    onError: (e: unknown) => toast.error("Upload failed", { description: (e as Error).message }),
  });

  const remove = useMutation({
    mutationFn: (f: QuoteFile) => quoteFilesApi.delete(quoteId, f.quoteFileId, f.rowVersion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["quote", quoteId, "files"] });
      toast.success("File removed");
      setPendingDelete(null);
    },
    onError: (e: unknown) => toast.error("Delete failed", { description: (e as Error).message }),
  });

  return (
    <div className="space-y-4">
      {canUpload && (
        <FileDropzone onFiles={(f) => {
          if (f.length > 0) upload.mutate(f);
        }} />
      )}

      {list.isLoading && <p>Loading…</p>}
      {list.isError && <p role="alert">Failed to load files.</p>}

      {list.data && list.data.length === 0 && (
        <p className="text-muted-foreground">No files attached.</p>
      )}

      {list.data && list.data.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="w-12">Thumb</th>
              <th>File</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {list.data.map(f => (
              <tr key={f.quoteFileId}>
                <td>
                  <FileThumbnail
                    url={f.hasThumbnail ? quoteFilesApi.thumbnailUrl(quoteId, f.quoteFileId) : null}
                    contentType={f.contentType}
                    alt={f.originalFileName}
                  />
                </td>
                <td>
                  <a
                    href={quoteFilesApi.downloadUrl(quoteId, f.quoteFileId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {f.originalFileName}
                  </a>
                </td>
                <td>{(f.sizeBytes / 1024).toFixed(1)} KB</td>
                <td>{formatDate(f.uploadedAtUtc)}</td>
                <td>
                  {canDelete && (
                    <button
                      type="button"
                      className="text-slate-400 hover:text-red-600 transition-colors"
                      aria-label="Delete File"
                      onClick={() => setPendingDelete(f)}
                    >
                      <X className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pendingDelete && (
        <div role="dialog" aria-labelledby="confirm-delete-title" className="rounded border p-3 bg-muted">
          <p id="confirm-delete-title" className="font-medium">
            Delete {pendingDelete.originalFileName}?
          </p>
          <p className="text-sm text-muted-foreground">This cannot be undone via the UI.</p>
          <div className="mt-2 flex gap-2">
            <Button
              variant="destructive"
              onClick={() => remove.mutate(pendingDelete)}
              disabled={remove.isPending}
            >
              Confirm
            </Button>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
